import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ServiceBase } from './service.base';
import { AuthService } from './auth.service';

/**
 * Standard API envelope. **HTTP 200 can carry `Success:false`** — this API answers handled
 * errors with 200 and puts the outcome in `Success`, so a caller that trusts the status code
 * renders a blank page with no error. Always check `Success`.
 */
export interface ResponseObject<T = any> {
  Success: boolean;
  ResponseMessage: string;
  ResponseData: T;
  Errors: string[];
}

/**
 * The artist's website as returned by `/ArtistWebsite`.
 *
 * ⚠️ The envelope properties here are PascalCase per the C# contract, but `SiteContent`,
 * `ShownIds` and `Uploads` hold the **client-owned document, verbatim and camelCase**. The API
 * binds them as `JToken` precisely so they round-trip byte-for-byte — typing them server-side
 * would have re-cased `siteTitle` to `SiteTitle` in transit and silently broken the renderer.
 * So `SiteContent` is deliberately untyped here: its shape belongs to the builder, not the API,
 * and new fields can be added without a backend release.
 */
/**
 * An enquiry sent from a published site. PascalCase: unlike `SiteContent`, this is an ordinary
 * typed DTO on the C# side, so it follows the usual contract.
 */
export interface WebsiteEnquiryRequest {
  /** The published site's slug — this is what decides who receives the message. */
  Identifier: string;
  /** The piece it is about, when raised from a work gallery. Omitted for the contact form. */
  ArtistProductId?: string | null;
  /** The page it was sent from, for the artist's context. */
  SourcePath?: string;
  SenderName?: string;
  SenderEmail: string;
  SenderPhone?: string;
  Message: string;
}

export interface ArtistWebsiteResponse {
  ArtistId: string;
  SiteContent: any;
  ShownIds: string[] | null;
  Uploads: string[] | null;
  IsPublished: boolean;
  Slug: string | null;
  ModifiedDate: string | null;
  PublishedDate: string | null;
}

/**
 * Website Builder persistence. The server is a per-artist JSON document store plus a publish
 * flag and a public read — it does not model or render the site.
 *
 * Draft and published are stored separately, so autosaving a draft never changes what a
 * visitor sees until {@link publish} is called again.
 */
@Injectable({
  providedIn: 'root'
})
export class ArtistWebsiteService extends ServiceBase {

  private api: string = '/ArtistWebsite';

  constructor(private http: HttpClient,
    authService: AuthService) {
    super(authService);
  }

  /**
   * Loads the artist's draft for the builder (owner or admin).
   *
   * `Success:true` with `ResponseData:null` means "no website yet" — that is the normal
   * first-run path, not an error, and the caller should seed from profile + artworks.
   */
  public getDraft(artistId: string): Observable<ResponseObject<ArtistWebsiteResponse | null>> {
    const url: string = environment.BASE_API_PATH + this.api + '/' + artistId;
    return this.http.get<ResponseObject<ArtistWebsiteResponse | null>>(url, this.RequestHeaders());
  }

  /**
   * Upserts the draft (Save and auto-save alike).
   *
   * `artistId` is omitted for an artist saving their own site: the server resolves the owner
   * from the token, which is both the simplest call and the safe one — a body-supplied id
   * reaching a write path is what made DW-018 an account-takeover vector. Pass it only for
   * admin preview, where it is checked against the caller rather than trusted.
   */
  public saveDraft(siteContent: any, shownIds: string[], uploads: string[],
    artistId?: string): Observable<ResponseObject<ArtistWebsiteResponse>> {
    const url: string = environment.BASE_API_PATH + this.api;
    const body: any = { SiteContent: siteContent, ShownIds: shownIds, Uploads: uploads };
    if (artistId) { body.ArtistId = artistId; }
    return this.http.put<ResponseObject<ArtistWebsiteResponse>>(url, body, this.RequestHeaders());
  }

  /**
   * Publishes the current draft to the public snapshot.
   *
   * `slug` is optional — omitted, the server defaults it to the artist's existing profile
   * username, so a published site is reachable without inventing a second namespace. A clash
   * returns a user-facing message that should be shown against the slug field, not as a
   * generic failure.
   */
  public publish(slug?: string, artistId?: string): Observable<ResponseObject<ArtistWebsiteResponse>> {
    const url: string = environment.BASE_API_PATH + this.api + '/publish';
    const body: any = {};
    if (slug) { body.Slug = slug; }
    if (artistId) { body.ArtistId = artistId; }
    return this.http.post<ResponseObject<ArtistWebsiteResponse>>(url, body, this.RequestHeaders());
  }

  /**
   * Sends an enquiry from a published site's contact form or work gallery — **no auth**.
   *
   * The recipient is not passed: the server resolves it from `Identifier`, the published site the
   * visitor is looking at. `ArtistProductId` names the piece when the enquiry came from a work
   * card; the server confirms it belongs to that site's artist and quietly drops it if not, rather
   * than refusing the message — losing a real enquiry because someone was on a stale page is worse
   * than losing the piece reference.
   *
   * Rate-limited server-side (5 per 5 minutes per IP), so a rejection is a plain failure envelope.
   */
  public submitEnquiry(request: WebsiteEnquiryRequest): Observable<ResponseObject<null>> {
    const url: string = environment.BASE_API_PATH + this.api + '/enquiry';
    return this.http.post<ResponseObject<null>>(url, request, this.RequestHeaders());
  }

  /**
   * Public read of a published site — **no auth**, so it works for a logged-out visitor.
   * `identifier` is the slug, falling back server-side to the artist's profile username.
   *
   * Deliberately does not send `RequestHeaders()`: the endpoint is anonymous, and attaching a
   * stale token to a public read gains nothing. `Success:false` here means "nothing published"
   * — fall back to the standard profile view rather than showing an error.
   */
  public getPublished(identifier: string): Observable<ResponseObject<ArtistWebsiteResponse | null>> {
    const url: string = environment.BASE_API_PATH + this.api + '/public/' + encodeURIComponent(identifier);
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.get<ResponseObject<ArtistWebsiteResponse | null>>(url, httpOptions);
  }

  /**
   * The artworks a published site displays — **no auth**.
   *
   * The site used to load these through `ArtistProduct/{artistId}/fetch/summary`, which requires
   * a token: logged in the grid filled, and to an actual visitor — the only audience a published
   * site has — every gallery was empty.
   *
   * The caller names the site, never the artworks. The server reads the ids from that site's own
   * published snapshot, so this cannot reach a piece the artist did not publish.
   */
  public getPublishedArtworks(identifier: string): Observable<ResponseObject<WebsiteArtwork[]>> {
    const url: string = environment.BASE_API_PATH + this.api
      + '/public/' + encodeURIComponent(identifier) + '/artworks';
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.get<ResponseObject<WebsiteArtwork[]>>(url, httpOptions);
  }
}

/**
 * One artwork as a published site shows it to a visitor with no account.
 *
 * Deliberately lean, and it is worth knowing what is NOT here: no ArtistRevenue (what the artist
 * nets after commission — the site had been showing that as the public price), no commission,
 * no consignment status. `AskingPrice` is the number the artist chose to show a buyer.
 */
export interface WebsiteArtwork {
  Id: string;
  Name: string;
  ProductImgUrl: string | null;
  Category: string | null;
  ArtTypeName: string | null;
  SeriesName: string | null;
  Length: number | null;
  Width: number | null;
  Height: number | null;
  Description: string | null;
  AskingPrice: number | null;
  IsSold: boolean;
}
